const LAST_ACTIVITY_KEY = 'lastActivityAt'
const SESSION_STARTED_KEY = 'sessionStartedAt'
const CHECK_INTERVAL_MS = 60 * 1000 // 1 minute
const ACTIVITY_THROTTLE_MS = 1000

const IDLE_TIMEOUT_MS = (Number(env('VUE_APP_IDLE_TIMEOUT_MINUTES')) || 15) * 60 * 1000 // 15 minutes
const MAX_SESSION_MS = (Number(env('VUE_APP_MAX_SESSION_HOURS')) || 12) * 60 * 60 * 1000 // 12 hours

const ACTIVITY_EVENTS = ['keydown', 'mousedown', 'touchstart', 'click', 'input']

let router = null
let checkIntervalId = null
let lastRecordedAt = 0
let isRunning = false
let isLoggingOut = false

const boundHandlers = {}

function getLastActivityAt() {
    return Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || 0
}

function getSessionStartedAt() {
    return Number(localStorage.getItem(SESSION_STARTED_KEY)) || 0
}

function clearActivityKeys() {
    localStorage.removeItem(LAST_ACTIVITY_KEY)
    localStorage.removeItem(SESSION_STARTED_KEY)
}

function redirectToLogin() {
    if (!router) {
        return
    }

    const currentRoute = router.currentRoute
    if (currentRoute && currentRoute.name === 'auth.login') {
        return
    }

    router.replace({ name: 'auth.login' })
}

function getAuthService() {
    return require('./AuthService').default
}

function expireSession() {
    if (isLoggingOut) {
        return
    }

    const AuthService = getAuthService()
    if (!AuthService.isLoggedIn()) {
        return
    }

    isLoggingOut = true

    Promise.resolve(AuthService.logOut())
        .then(() => {
            redirectToLogin()
        })
        .finally(() => {
            isLoggingOut = false
        })
}

function checkExpiry() {
    const AuthService = getAuthService()

    if (!AuthService.isLoggedIn()) {
        stop()
        return
    }

    const now = Date.now()
    const lastActivity = getLastActivityAt()
    const sessionStarted = getSessionStartedAt()

    if (sessionStarted && now - sessionStarted > MAX_SESSION_MS) {
        expireSession()
        return
    }

    if (lastActivity && now - lastActivity > IDLE_TIMEOUT_MS) {
        expireSession()
    }
}

function onStorageEvent(event) {
    if (event.key === LAST_ACTIVITY_KEY) {
        checkExpiry()
        return
    }

    if (event.key === 'idToken' && !localStorage.getItem('idToken')) {
        stop()
    }
}

function onVisibilityChange() {
    if (document.visibilityState === 'visible') {
        checkExpiry()
    }
}

function onActivity() {
    recordActivity()
}

function attachListeners() {
    ACTIVITY_EVENTS.forEach((event) => {
        boundHandlers[event] = onActivity
        document.addEventListener(event, boundHandlers[event], { passive: true })
    })

    boundHandlers.visibilitychange = onVisibilityChange
    document.addEventListener('visibilitychange', boundHandlers.visibilitychange)

    boundHandlers.storage = onStorageEvent
    window.addEventListener('storage', boundHandlers.storage)
}

function detachListeners() {
    ACTIVITY_EVENTS.forEach((event) => {
        if (boundHandlers[event]) {
            document.removeEventListener(event, boundHandlers[event])
        }
    })

    if (boundHandlers.visibilitychange) {
        document.removeEventListener('visibilitychange', boundHandlers.visibilitychange)
    }

    if (boundHandlers.storage) {
        window.removeEventListener('storage', boundHandlers.storage)
    }
}

function recordActivity() {
    if (!isRunning) {
        return
    }

    const AuthService = getAuthService()
    if (!AuthService.isLoggedIn()) {
        return
    }

    const now = Date.now()
    if (now - lastRecordedAt < ACTIVITY_THROTTLE_MS) {
        return
    }

    lastRecordedAt = now
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now))
}

function start() {
    if (isRunning) {
        return
    }

    isRunning = true

    const now = Date.now()
    if (!getLastActivityAt()) {
        localStorage.setItem(LAST_ACTIVITY_KEY, String(now))
    }
    lastRecordedAt = getLastActivityAt() || now

    attachListeners()
    checkIntervalId = setInterval(checkExpiry, CHECK_INTERVAL_MS)
    checkExpiry()
}

function stop() {
    isRunning = false

    detachListeners()

    if (checkIntervalId) {
        clearInterval(checkIntervalId)
        checkIntervalId = null
    }

    clearActivityKeys()
    lastRecordedAt = 0
}

export default {
    init(routerInstance) {
        router = routerInstance
    },

    start,

    stop,

    recordActivity,
}
